import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api from "../api/axios";
import useBrandingLogo from "../hooks/useBrandingLogo";
import "../styles/admin.css";

export default function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const logoUrl = useBrandingLogo();

  const emailFromState = location.state?.email || "";
  const otpFromState = location.state?.otp || "";

  const [email] = useState(emailFromState);
  const [otp] = useState(otpFromState);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!emailFromState || !otpFromState) {
      navigate("/forgot-password", { replace: true });
    }
  }, [emailFromState, otpFromState, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg("");
    setError("");

    const cleanPassword = String(newPassword || "");
    const cleanConfirm = String(confirmPassword || "");

    if (cleanPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    if (cleanPassword !== cleanConfirm) {
      setError("Passwords do not match.");
      return;
    }

    try {
      setSubmitting(true);

      const res = await api.post("/auth/forgot-password/reset-password", {
        email: String(email).trim().toLowerCase(),
        otp: String(otp).trim(),
        newPassword: cleanPassword,
      });

      setMsg(res?.data?.message || "Password reset successful.");

      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 1200);
    } catch (err) {
      console.error("Reset password error:", err?.response?.data?.message || err.message);
      setError(err?.response?.data?.message || "Failed to reset password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="admin-login-page">
      <section className="admin-forgot-shell">
        <div className="admin-forgot-card">
          <img
            src={logoUrl}
            alt="FleetX Logo"
            className="admin-forgot-logo"
          />

          <p className="admin-login-form__eyebrow">Password Recovery</p>
          <h1 className="admin-forgot-title">Reset Password</h1>
          <p className="admin-forgot-subtitle">
            Create a new password for your admin account.
          </p>

          <form onSubmit={handleSubmit} className="admin-login-form">
            <label className="admin-login-label">Email Address</label>
            <div className="admin-login-inputWrap">
              <span className="admin-login-inputIcon">✉</span>
              <input
                className="admin-login-input"
                type="email"
                value={email}
                readOnly
              />
            </div>

            <label className="admin-login-label">New Password</label>
            <div className="admin-login-inputWrap">
              <span className="admin-login-inputIcon">🔒</span>
              <input
                className="admin-login-input admin-login-input--password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                className="admin-login-toggle"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "🙈" : "👁"}
              </button>
            </div>

            <label className="admin-login-label">Confirm Password</label>
            <div className="admin-login-inputWrap">
              <span className="admin-login-inputIcon">🔒</span>
              <input
                className="admin-login-input"
                type={showPassword ? "text" : "password"}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>

            <button
              type="submit"
              className="admin-login-button"
              disabled={submitting}
            >
              {submitting ? "Resetting..." : "Reset Password"}
            </button>
          </form>

          {msg ? <p className="admin-forgot-success">{msg}</p> : null}
          {error ? <p className="admin-login-error">{error}</p> : null}

          <Link to="/login" className="admin-forgot-back">
            ← Back to Login
          </Link>
        </div>
      </section>
    </main>
  );
}
